import * as React from 'react';
import {
    ContextActionType,
    ModalContext,
    ModalFormTitle,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import { ColumnDatatable } from '@europrocurement/flexy-datatable';
import { OptionsJsonldOptionRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { useDispatch } from 'react-redux';

import { addItem, FaOptionIcon } from '@europrocurement/l2d-icons';
import { useOffreService } from '@europrocurement/l2d-domain';
import { useDefaultContextActions } from '@europrocurement/l2d-modules/hooks';
import { Typography } from '@mui/material';
import OfferOptionForm from './Form';
import { displayCustomValue, getStringColumn } from '../listings';
import ListingWithSearchAndPreview from '../components/ListingWithSearchAndPreview';
import { PossibleTypes } from '../../Societes';

type HTMLFieldsPreviewType =
    | 'select'
    | 'date'
    | 'radio'
    | 'text'
    | 'textarea'
    | 'number'
    | 'radio'
    | 'checkbox';

const OfferOptionPanel: React.FunctionComponent = function () {
    const { modalActions } = React.useContext(ModalContext);

    const { offerOptionModel: model } = useOffreService();
    const { backContextActions } = useDefaultContextActions();

    const dispatch = useDispatch();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const reloadList = () => dispatch(model.datasourcesThunks.main.getData({}) as any);

    const getHtmlFieldTypeLabel = (htmlFieldType?: HTMLFieldsPreviewType) => {
        switch (htmlFieldType) {
            case 'select':
                return "Liste d'options";
            case 'checkbox':
                return 'Case à cocher';
            case 'text':
                return 'Champ texte';
            case 'textarea':
                return 'Zone de texte';
            case 'number':
                return 'Nombre';
            case 'date':
                return 'Champ date';
            case 'radio':
                return 'Bouton radio';
            default:
                return '';
        }
    };

    const listingButton = {
        label: 'Créer une nouvelle option',
        icon: addItem,
        action: () =>
            modalActions.call(
                <>
                    <ModalFormTitle
                        icon={model.icon}
                        title="Création d'une option"
                    />
                    <OfferOptionForm
                        isCreate
                        afterSubmit={reloadList}
                    />
                </>,
            ),
    };

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer une nouvelle option',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: listingButton.action,
            },
            ...backContextActions,
        ],
        [backContextActions, listingButton.action],
    );

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<OptionsJsonldOptionRead>[] = [
        getStringColumn('id'),
        getStringColumn('libelle', 'Libellé'),
        getStringColumn('description'),
        displayCustomValue('domaine', (domaine) => domaine.libelle, 'Domaine'),
        displayCustomValue(
            'visibleFront',
            (visibleFront) => (visibleFront ? 'oui' : 'non'),
            'Visible',
        ),
        displayCustomValue('obligatoire', (required) => (required ? 'oui' : 'non'), 'Obligatoire'),
        {
            label: 'Type de champ HTML',
            displayLabel: true,
            render: ({ htmlFieldType }) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {getHtmlFieldTypeLabel(htmlFieldType as HTMLFieldsPreviewType)}
                </Typography>
            ),
            isDisplayed: true,
            sortable: false,
            field: 'htmlFieldType',
        },
    ];

    return (
        <ListingWithSearchAndPreview
            listingTitle="Liste des options"
            listingButton={listingButton}
            listingColumns={columns}
            form={OfferOptionForm}
            model={model}
            filtersControl={false}
            searchBarType={PossibleTypes.Option}
        />
    );
};

export default OfferOptionPanel;
